import { isWeb } from "@memorang/helpers/platform";
import { createAudioPlayer, setAudioModeAsync } from "expo-audio";
import type { AudioStatus } from "expo-audio";
import { useSyncExternalStore } from "react";

export type Track = {
	url: string;
	title: string;
	artist: string;
	artwork: string;
	album?: string;
};

type ProgressState = {
	position: number;
	duration: number;
	buffered: number;
};

type TrackPlayerEventHandler = () => Promise<void> | undefined;
type TrackPlayerSubscription = { remove: () => undefined };

type AudioPlayerClient = {
	setupPlayer: () => Promise<void>;
	updateOptions: (options: { capabilities: number[] }) => Promise<void>;
	getQueue: () => Promise<Track[]>;
	getActiveTrackIndex: () => Promise<number | undefined>;
	skip: (index: number) => Promise<void>;
	reset: () => Promise<void>;
	add: (tracks: Track[]) => Promise<void>;
	play: () => Promise<void>;
	pause: () => Promise<void>;
	skipToPrevious: () => Promise<void>;
	getTrack: (index: number) => Promise<Track | undefined>;
	skipToNext: () => Promise<void>;
	seekTo: (position: number) => Promise<void>;
	addEventListener: (
		event: string,
		handler: TrackPlayerEventHandler,
	) => TrackPlayerSubscription | undefined;
};

const EMPTY_PROGRESS: ProgressState = {
	position: 0,
	duration: 0,
	buffered: 0,
};

const isNative = !isWeb;

const progressListeners = new Set<() => void>();

const emitProgress = () => {
	for (const listener of progressListeners) {
		listener();
	}
};

let currentProgress: ProgressState = EMPTY_PROGRESS;

const setProgress = (next: ProgressState) => {
	currentProgress = next;
	emitProgress();
};

const statusToProgress = (status: AudioStatus): ProgressState => {
	if (!status.isLoaded) {
		return EMPTY_PROGRESS;
	}
	return {
		position: status.currentTime,
		duration: status.duration,
		buffered: status.currentTime,
	};
};

type PlayerState = {
	queue: Track[];
	activeTrackIndex: number | undefined;
	isPlaying: boolean;
	player: ReturnType<typeof createAudioPlayer> | null;
	/** Monotonically increasing ID to detect stale listeners. */
	loadId: number;
};

const state: PlayerState = {
	queue: [],
	activeTrackIndex: undefined,
	isPlaying: false,
	player: null,
	loadId: 0,
};

const createNoopSubscription = (): TrackPlayerSubscription => ({
	remove: () => undefined,
});

const releasePlayer = () => {
	if (!state.player) {
		return;
	}
	const prev = state.player;
	state.player = null;
	// Stop output immediately. On iOS, `remove()` tears down the native player
	// asynchronously (`SharedObjectRegistry.delete` uses `lockQueue.async`), so
	// `sharedObjectWillRelease` → `AVPlayer.pause()` can run *after* the next
	// track's player has already started — two streams or stuck "ghost" audio.
	try {
		prev.pause();
		if (isNative) {
			prev.clearLockScreenControls();
		}
	} catch {
		// Player may already be in an invalid state; ensure remove() still runs.
	}
	try {
		prev.remove();
	} catch {
		// Best-effort cleanup — the native object may already be released.
	}
};

const loadTrack = (index: number, shouldPlay: boolean): Promise<void> => {
	const track = state.queue[index];
	if (!track?.url) {
		return Promise.resolve();
	}

	releasePlayer();
	state.loadId += 1;
	const myLoadId = state.loadId;

	const player = createAudioPlayer({ uri: track.url });

	let pendingPlay = shouldPlay;
	let didStartPlay = false;

	player.addListener("playbackStatusUpdate", (status: AudioStatus) => {
		if (myLoadId !== state.loadId) {
			return;
		}

		setProgress(statusToProgress(status));

		if (!status.isLoaded) {
			return;
		}

		if (pendingPlay && !didStartPlay) {
			didStartPlay = true;
			pendingPlay = false;
			player.play();
			state.isPlaying = true;

			if (isNative) {
				player.setActiveForLockScreen(true, {
					title: track.title,
					artist: track.artist,
					artworkUrl: track.artwork,
				});
			}
			return;
		}

		state.isPlaying = status.playing;

		if (!status.didJustFinish) {
			return;
		}

		const nextIndex = index + 1;
		if (nextIndex >= state.queue.length) {
			state.isPlaying = false;
			return;
		}

		queueMicrotask(() => {
			if (myLoadId !== state.loadId) {
				return;
			}
			loadTrack(nextIndex, true);
		});
	});

	state.player = player;
	state.activeTrackIndex = index;
	state.isPlaying = shouldPlay;

	if (shouldPlay && isWeb) {
		player.play();
	}

	return Promise.resolve();
};

const audioPlayerInstance: AudioPlayerClient = {
	setupPlayer: async () => {
		await setAudioModeAsync({
			playsInSilentMode: true,
			shouldPlayInBackground: isNative,
			interruptionMode: "doNotMix",
			allowsRecording: false,
			shouldRouteThroughEarpiece: false,
		});
	},
	updateOptions: async () => undefined,
	getQueue: async () => state.queue,
	getActiveTrackIndex: async () => state.activeTrackIndex,
	skip: async (index: number) => {
		if (index < 0 || index >= state.queue.length) {
			return;
		}
		await loadTrack(index, state.isPlaying);
	},
	reset: () => {
		releasePlayer();
		state.loadId += 1;
		state.queue = [];
		state.activeTrackIndex = undefined;
		state.isPlaying = false;
		setProgress(EMPTY_PROGRESS);
		return Promise.resolve();
	},
	add: (tracks: Track[]) => {
		state.queue = state.queue.concat(tracks);
		return Promise.resolve();
	},
	play: async () => {
		if (!state.queue.length) {
			return;
		}
		if (!state.player) {
			await loadTrack(state.activeTrackIndex ?? 0, true);
			return;
		}
		state.player.play();
		state.isPlaying = true;
	},
	pause: () => {
		if (!state.player) {
			return Promise.resolve();
		}
		state.player.pause();
		state.isPlaying = false;
		return Promise.resolve();
	},
	skipToPrevious: async () => {
		if (state.activeTrackIndex === undefined || state.activeTrackIndex <= 0) {
			return;
		}
		await loadTrack(state.activeTrackIndex - 1, state.isPlaying);
	},
	getTrack: async (index: number) => state.queue[index],
	skipToNext: async () => {
		if (state.activeTrackIndex === undefined) {
			return;
		}
		const nextIndex = state.activeTrackIndex + 1;
		if (nextIndex >= state.queue.length) {
			return;
		}
		await loadTrack(nextIndex, state.isPlaying);
	},
	seekTo: async (position: number) => {
		if (!state.player) {
			return;
		}
		await state.player.seekTo(Math.max(position, 0));
	},
	addEventListener: () => createNoopSubscription(),
};

export const audioPlayer = audioPlayerInstance;

export const isAudioPlayerEnabled = true;

export const setupAudioPlayer = async () => {
	await audioPlayer.setupPlayer();
};

const subscribeToProgress = (listener: () => void) => {
	progressListeners.add(listener);
	return () => {
		progressListeners.delete(listener);
	};
};

const getProgressSnapshot = () => currentProgress;

export const useAudioProgress = (): ProgressState =>
	useSyncExternalStore(
		subscribeToProgress,
		getProgressSnapshot,
		getProgressSnapshot,
	);
